/**
 * Site shell: navigation, hero scenery and the small reusable pieces the routes
 * share. All "photography" is CSS-generated scenery from the shared theme — no
 * real course imagery is bundled or referenced.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { BRAND, HERO_SCENES, type HeroScene } from '@fairwaylive/shared';

export function Logo({ compact = false }: { compact?: boolean }) {
  return (
    <Link to="/" className="group flex items-center gap-2.5" aria-label={`${BRAND.name} home`}>
      <span className="relative grid h-8 w-8 place-items-center overflow-hidden rounded-[9px] bg-gradient-to-br from-[var(--color-fairway-400)] to-[var(--color-fairway-500)]">
        {/* Abstract flag-and-green mark — original, not derived from any product. */}
        <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
          <path d="M7 20V4l10 3.2L7 10.6" fill="var(--color-gold-500)" />
          <path d="M7 20V4" stroke="var(--color-chalk)" strokeWidth="1.6" strokeLinecap="round" />
          <ellipse cx="12.5" cy="19.4" rx="7.5" ry="1.9" fill="var(--color-fairway-300)" opacity="0.5" />
        </svg>
      </span>
      {!compact ? (
        <span className="board-title text-[19px] font-bold leading-none tracking-tight">
          Fairway<span className="text-[var(--color-fairway-300)]">Live</span>
        </span>
      ) : null}
    </Link>
  );
}

const NAV = [
  { to: '/demo', label: 'Live demo' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/rate-card', label: 'Advertise' },
  { to: '/sponsor', label: 'Sponsor portal' },
];

export function TopNav() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-30 border-b border-[color-mix(in_oklab,var(--color-fairway-400)_18%,transparent)] bg-[color-mix(in_oklab,var(--color-fairway-900)_88%,transparent)] backdrop-blur">
      <div className="mx-auto flex max-w-[1240px] items-center justify-between px-4 py-3">
        <Logo />
        <nav className="hidden items-center gap-1 md:flex">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? 'bg-[color-mix(in_oklab,var(--color-fairway-400)_18%,transparent)] text-[var(--color-fairway-300)]' : 'text-[var(--color-muted)] hover:text-[var(--color-chalk)]'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link to="/login" className="btn btn-ghost ml-2 !py-2">
            Sign in
          </Link>
          <Link to="/pricing" className="btn btn-primary !py-2">
            Start free
          </Link>
        </nav>
        <button type="button" className="btn btn-ghost !px-3 !py-2 md:hidden" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? '✕' : '☰'}
        </button>
      </div>
      {open ? (
        <nav className="border-t border-[color-mix(in_oklab,var(--color-fairway-400)_18%,transparent)] px-4 pb-4 md:hidden">
          {NAV.map((item) => (
            <Link key={item.to} to={item.to} onClick={() => setOpen(false)} className="block py-2.5 text-sm text-[var(--color-chalk)]">
              {item.label}
            </Link>
          ))}
          <div className="mt-2 flex gap-2">
            <Link to="/login" onClick={() => setOpen(false)} className="btn btn-ghost flex-1">
              Sign in
            </Link>
            <Link to="/pricing" onClick={() => setOpen(false)} className="btn btn-primary flex-1">
              Start free
            </Link>
          </div>
        </nav>
      ) : null}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="mt-20 border-t border-[color-mix(in_oklab,var(--color-fairway-400)_16%,transparent)] bg-[var(--color-fairway-950)]">
      <div className="mx-auto grid max-w-[1240px] gap-8 px-4 py-12 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo />
          <p className="mt-3 max-w-[26ch] text-sm text-[var(--color-muted)]">{BRAND.tagline}</p>
        </div>
        <FooterColumn
          title="Product"
          links={[
            ['Live demo', '/demo'],
            ['Clubhouse display', '/demo/bigscreen'],
            ['Scoring formats', '/pricing#formats'],
            ['Pricing', '/pricing'],
          ]}
        />
        <FooterColumn
          title="Sponsors"
          links={[
            ['Rate card', '/rate-card'],
            ['Sponsor portal', '/sponsor'],
            ['Inventory & zones', '/rate-card#zones'],
          ]}
        />
        <div>
          <h3 className="eyebrow mb-3">Notes</h3>
          <p className="text-xs leading-relaxed text-[var(--color-muted-dim)]">
            All clubs, tournaments, players and sponsors shown anywhere on this site are fictional. Scenery is
            generated, not photographed. Handicap indexes calculated here are our own Playing Ability index and are
            not an official national handicap.
          </p>
        </div>
      </div>
      <div className="border-t border-[color-mix(in_oklab,var(--color-fairway-400)_12%,transparent)] px-4 py-5 text-center text-xs text-[var(--color-muted-dim)]">
        © {new Date().getFullYear()} {BRAND.name}. Built for South African golf.
      </div>
    </footer>
  );
}

function FooterColumn({ title, links }: { title: string; links: Array<[string, string]> }) {
  return (
    <div>
      <h3 className="eyebrow mb-3">{title}</h3>
      <ul className="space-y-2">
        {links.map(([label, to]) => (
          <li key={to}>
            <Link to={to} className="text-sm text-[var(--color-muted)] transition-colors hover:text-[var(--color-fairway-300)]">
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Rotating hero scenery. Each scene is a layered CSS gradient standing in for a
 * South African golf landscape — escarpment dawn, bushveld noon, coastal wind,
 * sunset finish — with a fictional venue name.
 */
export function HeroCarousel({
  sceneKeys,
  intervalSeconds = 7,
  height = 'h-[420px]',
  children,
}: {
  sceneKeys?: string[];
  intervalSeconds?: number;
  height?: string;
  children?: ReactNode;
}) {
  const scenes = useMemo<HeroScene[]>(() => {
    if (!sceneKeys?.length) return HERO_SCENES;
    const picked = sceneKeys.map((key) => HERO_SCENES.find((s) => s.key === key)).filter(Boolean) as HeroScene[];
    return picked.length ? picked : HERO_SCENES;
  }, [sceneKeys]);

  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (scenes.length < 2) return;
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % scenes.length), intervalSeconds * 1000);
    return () => window.clearInterval(timer);
  }, [intervalSeconds, scenes.length]);

  return (
    <div className={`relative isolate w-full overflow-hidden ${height}`}>
      {scenes.map((scene, i) => (
        <div
          key={scene.key}
          className="absolute inset-0 transition-opacity duration-[1400ms] ease-in-out motion-reduce:transition-none"
          style={{ background: scene.css, opacity: i === index ? 1 : 0 }}
          aria-hidden={i !== index}
        />
      ))}
      {/* Legibility scrim so overlaid type always passes contrast. */}
      <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-fairway-900)] via-[rgba(8,17,13,0.45)] to-transparent" />
      <div className="relative z-10 flex h-full flex-col justify-end">{children}</div>
      <div className="absolute bottom-3 right-4 z-10 flex items-center gap-3">
        <span className="rounded bg-[rgba(2,8,5,0.55)] px-2 py-1 text-[10px] text-[var(--color-sand)]">
          {scenes[index]?.title} · {scenes[index]?.subtitle}
        </span>
        <div className="flex gap-1.5">
          {scenes.map((scene, i) => (
            <button
              key={scene.key}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Show ${scene.title}`}
              className={`h-1.5 rounded-full transition-all ${i === index ? 'w-6 bg-[var(--color-fairway-300)]' : 'w-1.5 bg-[rgba(244,247,244,0.4)]'}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export function DemoBadge() {
  return (
    <div className="flex items-center gap-2 rounded-full border border-[var(--color-gold-500)] bg-[color-mix(in_oklab,var(--color-gold-500)_16%,transparent)] px-3 py-1">
      <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--color-gold-500)]" />
      <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[var(--color-gold-400)]">Demo mode — sample data</span>
    </div>
  );
}

export function LiveDot({ transport }: { transport: string }) {
  const streaming = transport === 'stream';
  return (
    <span className="flex items-center gap-1.5" title={streaming ? 'Live stream connected' : `Polling for updates (${transport})`}>
      <span
        className={`h-2 w-2 rounded-full ${
          streaming ? 'animate-pulse bg-[var(--color-fairway-300)]' : transport === 'error' ? 'bg-[var(--color-rose-sun)]' : 'bg-[var(--color-gold-500)]'
        }`}
      />
      <span className="text-[11px] uppercase tracking-wider text-[var(--color-muted)]">
        {streaming ? 'Live' : transport === 'error' ? 'Reconnecting' : 'Live · polling'}
      </span>
    </span>
  );
}

export function Section({ title, eyebrow, children, id }: { title?: string; eyebrow?: string; children: ReactNode; id?: string }) {
  return (
    <section id={id} className="mx-auto max-w-[1240px] px-4 py-12">
      {eyebrow ? <p className="eyebrow mb-2">{eyebrow}</p> : null}
      {title ? <h2 className="board-title mb-6 text-[28px] font-bold sm:text-[34px]">{title}</h2> : null}
      {children}
    </section>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-sm text-[var(--color-muted)]">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--color-fairway-400)] border-t-transparent" />
      {label}…
    </div>
  );
}

export function ErrorNote({ message, hint }: { message: string; hint?: string }) {
  return (
    <div className="card border-[color-mix(in_oklab,var(--color-rose-sun)_40%,transparent)] p-5">
      <p className="text-sm font-semibold text-[var(--color-rose-sun)]">{message}</p>
      {hint ? <p className="mt-1 text-xs text-[var(--color-muted)]">{hint}</p> : null}
    </div>
  );
}
